import client from './client';

/**
 * User permissions returned from the backend.
 *
 * IMPORTANT: All permission logic is driven by the backend.
 * DO NOT implement frontend-based permission checks - always use these values.
 */
export interface UserPermissions {
  /** Can this user leave comments? (False for read-only roles such as viewer) */
  can_comment: boolean;

  /** Can this user approve media plans? (Planners and Admins only) */
  can_approve_media_plan: boolean;

  /** List of campaign fields this user can edit */
  editable_campaign_fields: string[];

  /** Permission level for Asset Modal: 'view_only' | 'comment_only' | 'all_editable' */
  asset_modal_permission: 'view_only' | 'comment_only' | 'all_editable';

  /** Permission level for Status Overlay: 'view_only' | 'comment_only' | 'all_editable' */
  status_overlay_permission: 'view_only' | 'comment_only' | 'all_editable';

  /** Can this user archive/unarchive campaigns? */
  can_archive: boolean;

  /** Can this user download assets? (Activation and Admins only) */
  can_download_assets: boolean;

  /** Can this user view the analytics dashboard? (Admins only) */
  can_view_dashboard: boolean;

  /** Visible guideline tabs for this user (e.g., ["overview", "planner"]) */
  visible_guideline_tabs: string[];

  /** User's role (for display purposes only) */
  role: string;

  /** Can this user approve preview links? */
  can_approve_preview_link: boolean;

  /** Is this user an admin? */
  is_admin: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string; // Role is managed by backend, UI should not use for permissions
  department?: string;
  is_active: boolean;
  last_login?: string;
  /** User permissions - use these for all permission checks */
  permissions?: UserPermissions;
  market?: string[];
  channel?: string[];
  /** Suite apps this user may open. See `useAppAccess`. */
  app_access?: string[];
}

export const getCurrentUser = async (): Promise<User> => {
  const response = await client.get<User>('/users/me');
  return response.data;
};

/**
 * Get current user's permissions.
 *
 * Use this to determine what UI elements to show/enable.
 * All permissions are driven by the backend - DO NOT implement
 * frontend-based permission logic.
 */
export const getPermissions = async (): Promise<UserPermissions> => {
  const response = await client.get<UserPermissions>('/users/me/permissions');
  return response.data;
};

export const updateUserPreferences = async (
  preferences: Record<string, unknown>,
): Promise<{ message: string }> => {
  const response = await client.put<{ message: string }>('/users/me/preferences', {
    preferences,
  });
  return response.data;
};

export const getAllUsers = async (): Promise<User[]> => {
  const response = await client.get<User[]>('/users/all');
  return response.data;
};

export const syncUsersFromConfig = async (): Promise<{ message: string }> => {
  const response = await client.post<{ message: string }>('/users/sync-from-config');
  return response.data;
};

// ============================================================================
// Mention Notifications (lightweight bell — queries comment_mentions)
// ============================================================================

/** A single @mention of the current user in a campaign comment. */
export interface MentionNotification {
  id: string;
  comment_text: string;
  comment_stage: string;
  author_name: string;
  author_email: string;
  campaign_id: string; // Human-readable, for deep-linking
  campaign_name: string;
  created_at: string; // ISO datetime
}

export interface MentionsResponse {
  mentions: MentionNotification[];
  total_count: number;
  unread_count: number;
  last_seen_at: string | null; // Mentions' own read state, separate from notifications
}

/** Fetch recent @mentions for the current user. */
export const getMyMentions = async (limit: number = 20): Promise<MentionsResponse> => {
  const response = await client.get<MentionsResponse>('/users/me/mentions', {
    params: { limit },
  });
  return response.data;
};

/** Mark @mentions as seen. Leaves the general notifications read state alone. */
export const markMentionsSeen = async (): Promise<{ last_seen_at: string }> => {
  const response = await client.patch<{ last_seen_at: string }>('/users/me/mentions/seen');
  return response.data;
};

// ============================================================================
// Waiting on you (Suite Home stat)
// ============================================================================

/** `count` is null when the user's role has no queue of its own (admin, viewer). */
export const getWaitingOnYou = async (): Promise<{ count: number | null }> => {
  const response = await client.get<{ count: number | null }>('/users/me/waiting-on-you');
  return response.data;
};

// ============================================================================
// In-App Notifications (expanded — all event types, role/market filtered)
// ============================================================================

/** A single in-app notification from campaign_events. */
export interface InAppNotification {
  id: string;
  event_type: string;
  title: string;
  item_text: string;
  actor_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  link_path?: string;
  link_param?: string;
  link_value?: string;
  created_at: string; // ISO datetime
}

export interface NotificationsResponse {
  notifications: InAppNotification[];
  total_count: number;
  last_seen_at: string | null; // ISO datetime — for unread count
}

/** Fetch in-app notifications for the current user. */
export const getMyNotifications = async (limit: number = 30): Promise<NotificationsResponse> => {
  const response = await client.get<NotificationsResponse>('/users/me/notifications', {
    params: { limit },
  });
  return response.data;
};

/** Mark notifications as seen (server-side read state). */
export const markNotificationsSeen = async (): Promise<{ last_seen_at: string }> => {
  const response = await client.patch<{ last_seen_at: string }>('/users/me/notifications/seen');
  return response.data;
};

// ============================================================================
// User Management API (Admin UI)
// ============================================================================

/** Full user details for management UI */
export interface UserManagement {
  id: string;
  email: string;
  name: string;
  role: string;
  department?: string;
  market?: string[];
  functional_role?: string;
  is_active: boolean;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
}

/** Request to create a new user */
export interface CreateUserRequest {
  email: string;
  name: string;
  role: string;
  department?: string;
  market?: string[];
  functional_role?: string;
}

/** Request to update an existing user */
export interface UpdateUserRequest {
  name?: string;
  role?: string;
  department?: string;
  market?: string[];
  functional_role?: string;
  is_active?: boolean;
}

/** Check if current user can manage users */
export const getCanManageUsers = async (): Promise<{ can_manage_users: boolean }> => {
  const response = await client.get<{ can_manage_users: boolean }>('/users/me/can-manage-users');
  return response.data;
};

/** Get all users for management UI */
export const getAllUsersForManagement = async (): Promise<UserManagement[]> => {
  const response = await client.get<UserManagement[]>('/users/management/all');
  return response.data;
};

/** Create a new user */
export const createUser = async (data: CreateUserRequest): Promise<UserManagement> => {
  const response = await client.post<UserManagement>('/users', data);
  return response.data;
};

/** Update an existing user */
export const updateUser = async (
  userId: string,
  data: UpdateUserRequest,
): Promise<UserManagement> => {
  const response = await client.put<UserManagement>(`/users/${userId}`, data);
  return response.data;
};
